import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Sidebar from '@/components/Sidebar';
import CalculationBreakdown from '@/components/CalculationBreakdown';
import ImpositionSelector from '@/components/ImpositionSelector';
import api from '@/lib/api';
import {
  applyImpositionDerivedValues,
  getImpositionDerivedValues,
  getImpositionOutputKeys,
  normalizeImpositionConfig,
} from '@/lib/impositionVariables';
import '@/pages/Products.css';
import '@/pages/Clients.css';
import '@/components/SheetImposition.css';

const formatMoney = (n) => `$${(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const PRODUCT_CALCULATOR_REVISTA = 'revista';
const PRODUCT_CALCULATOR_CUADERNO = 'cuaderno';
const PRODUCT_CALCULATOR_FACTURAS = 'facturas';
const INVOICE_COPY_ORDER = [
  { label: 'Original', suffix: 'original' },
  { label: 'Duplicado', suffix: 'duplicado' },
  { label: 'Triplicado', suffix: 'triplicado' },
  { label: 'Cuatriplicado', suffix: 'cuatriplicado' },
];

const formatSelectOptionLabel = (variable, option) => {
  const unit = (variable?.unit || '').trim().toLowerCase();
  const value = Number(option?.value);
  if (!Number.isFinite(value)) return option?.label || '';
  if (unit === '$') return `${option.label} (${formatMoney(value)})`;
  if (unit && !['copia', 'copias'].includes(unit)) {
    return `${option.label} (${value.toLocaleString('es-AR')} ${variable.unit})`;
  }
  return option.label;
};

const getInvoiceCopies = (product) => {
  const config = product?.calculator_config || {};
  const configuredCopies = Array.isArray(config.copies) ? config.copies : [];
  if (configuredCopies.length > 0) return configuredCopies;
  return INVOICE_COPY_ORDER.map(copy => ({
    key: copy.suffix,
    label: copy.label,
    paper_variable: `papel${copy.suffix}`,
    grammage_variable: `gramaje${copy.suffix}`,
    colors_variable: `colores${copy.suffix}`,
    plates_variable: `chapas${copy.suffix}`,
    active_variable: `activo${copy.suffix}`,
  }));
};

const getInvoiceCopyCount = (product, values, variables = []) => {
  if (product?.calculator_type !== PRODUCT_CALCULATOR_FACTURAS) return INVOICE_COPY_ORDER.length;
  const config = product?.calculator_config || {};
  const key = config.copy_count_variable || 'tipofactura';
  const variable = variables.find(v => v.key === key);
  const rawValue = values?.[key] ?? variable?.options?.[0]?.label ?? variable?.default_value ?? 1;
  const numericValue = Number(rawValue);
  if (Number.isFinite(numericValue) && numericValue > 0) {
    return Math.max(1, Math.min(INVOICE_COPY_ORDER.length, Math.round(numericValue)));
  }
  const normalized = String(rawValue || '').trim().toLowerCase();
  const matchedOption = (variable?.options || []).find(opt => (
    String(opt.label || '').trim().toLowerCase() === normalized
  ));
  if (matchedOption) {
    return getInvoiceCopyCount(product, { [key]: matchedOption.value }, variables);
  }
  const matchedIndex = INVOICE_COPY_ORDER.findIndex(copy => copy.label.toLowerCase() === normalized);
  return matchedIndex >= 0 ? matchedIndex + 1 : 1;
};

const getActiveInvoiceCopies = (product, values, variables = []) => (
  getInvoiceCopies(product)
    .slice(0, getInvoiceCopyCount(product, values, variables))
    .map((copy, index) => ({
      ...copy,
      key: copy.key || INVOICE_COPY_ORDER[index]?.suffix || `copia-${index + 1}`,
      label: copy.label || INVOICE_COPY_ORDER[index]?.label || `Copia ${index + 1}`,
      index,
    }))
);

const getInactiveInvoiceKeys = (product, values, variables = []) => {
  if (product?.calculator_type !== PRODUCT_CALCULATOR_FACTURAS) return new Set();
  const count = getInvoiceCopyCount(product, values, variables);
  const keys = new Set();
  getInvoiceCopies(product).slice(count).forEach((copy, offset) => {
    const fallback = INVOICE_COPY_ORDER[count + offset] || {};
    [
      copy.paper_variable,
      copy.grammage_variable,
      copy.colors_variable,
      copy.plates_variable,
      copy.active_variable,
      fallback.suffix ? `papel${fallback.suffix}` : null,
      fallback.suffix ? `gramaje${fallback.suffix}` : null,
      fallback.suffix ? `colores${fallback.suffix}` : null,
      fallback.suffix ? `chapas${fallback.suffix}` : null,
      fallback.suffix ? `activo${fallback.suffix}` : null,
    ].filter(Boolean).forEach(key => keys.add(key));
  });
  return keys;
};

const applyInvoiceBudgetValues = (product, values, variables = []) => {
  if (product?.calculator_type !== PRODUCT_CALCULATOR_FACTURAS) return values;
  const next = { ...(values || {}) };
  getInactiveInvoiceKeys(product, values, variables).forEach(key => {
    if (/^(gramaje|colores|chapas|activo)/i.test(key)) next[key] = 0;
  });
  return next;
};

const applyInvoiceCopyImpositions = (product, values, choices = {}, variables = []) => {
  if (product?.calculator_type !== PRODUCT_CALCULATOR_FACTURAS) return values;
  let next = { ...(values || {}) };
  const activeKeys = new Set(getActiveInvoiceCopies(product, values, variables).map(copy => copy.key));
  const activeDerived = [];
  getInvoiceCopies(product).forEach((copy, index) => {
    const normalizedCopy = {
      ...copy,
      key: copy.key || INVOICE_COPY_ORDER[index]?.suffix || `copia-${index + 1}`,
      label: copy.label || INVOICE_COPY_ORDER[index]?.label || `Copia ${index + 1}`,
    };
    const outputKeys = {
      sheets: normalizedCopy.sheets_variable || `pliegos${normalizedCopy.key}`,
      motherSheet: normalizedCopy.mother_sheet_variable || `hojamadre${normalizedCopy.key}`,
      spoilage: normalizedCopy.spoilage_variable || `desperdicio${normalizedCopy.key}`,
      sheetWidth: normalizedCopy.sheet_width_variable || `anchohoja${normalizedCopy.key}`,
      sheetHeight: normalizedCopy.sheet_height_variable || `largohoja${normalizedCopy.key}`,
    };
    const saved = choices[normalizedCopy.key];
    if (!activeKeys.has(normalizedCopy.key) || !saved?.choice || !saved?.config) {
      Object.values(outputKeys).forEach(key => {
        next[key] = Number(next[key]) || 0;
      });
      return;
    }
    const configWithoutSpoilage = {
      ...saved.config,
      spoilageSheets: 0,
      spoilage_sheets: 0,
    };
    const derived = getImpositionDerivedValues(saved.choice, configWithoutSpoilage, {
      sheets_variable: outputKeys.sheets,
      mother_sheet_variable: outputKeys.motherSheet,
      spoilage_variable: outputKeys.spoilage,
      sheet_width_variable: outputKeys.sheetWidth,
      sheet_height_variable: outputKeys.sheetHeight,
    });
    activeDerived.push({
      outputKeys,
      saved,
      derived,
      baseMotherSheets: Math.max(0, Number(derived[outputKeys.motherSheet]) || 0),
      generatedSheetsPerMother: Math.max(1, Number(saved.config?.sourceCut?.sheetCount) || 1),
    });
  });

  const totalSpoilageMotherSheets = activeDerived.length > 0
    ? Math.max(0, Math.ceil(Number(activeDerived[0].saved.config?.spoilageSheets ?? activeDerived[0].saved.config?.spoilage_sheets) || 0))
    : 0;
  const totalBaseMotherSheets = activeDerived.reduce((sum, item) => sum + item.baseMotherSheets, 0);
  let allocatedSpoilage = 0;

  activeDerived.forEach((item, index) => {
    let spoilageForCopy = 0;
    if (totalSpoilageMotherSheets > 0) {
      if (index === activeDerived.length - 1) {
        spoilageForCopy = totalSpoilageMotherSheets - allocatedSpoilage;
      } else if (totalBaseMotherSheets > 0) {
        spoilageForCopy = Math.floor((item.baseMotherSheets / totalBaseMotherSheets) * totalSpoilageMotherSheets);
      }
    }
    allocatedSpoilage += spoilageForCopy;
    next = {
      ...next,
      ...item.derived,
      [item.outputKeys.spoilage]: spoilageForCopy,
      [item.outputKeys.motherSheet]: item.baseMotherSheets + spoilageForCopy,
      [item.outputKeys.sheets]: (Number(item.derived[item.outputKeys.sheets]) || 0) + (spoilageForCopy * item.generatedSheetsPerMother),
    };
  });
  return next;
};

const normalizeMagazineElements = (config = {}) => (
  (config.elements || []).filter(element => element?.page_count_variable)
);

const isPageCalculatorProduct = (product) => (
  product?.calculator_type === PRODUCT_CALCULATOR_REVISTA
  || product?.calculator_type === PRODUCT_CALCULATOR_CUADERNO
);

const DEFAULT_COVER_CONFIG = {
  enabled_variable: 'tienetapa',
  front_colors_variable: 'colortapafrente',
  back_colors_variable: 'colortapadorso',
  plate_mode_variable: 'modochapastapa',
  plates_total_variable: 'chapastapa',
  print_sheets_variable: 'pliegostapa',
  mother_sheet_variable: 'hojamadretapa',
  spoilage_variable: 'desperdiciotapa',
  sheet_width_variable: 'anchohojatapa',
  sheet_height_variable: 'largohojatapa',
  pages_per_sheet_variable: 'paginasporpliegotapa',
};

const normalizeCoverConfig = (config = {}) => ({
  ...DEFAULT_COVER_CONFIG,
  ...(config || {}),
});

const getCoverConfig = (product) => normalizeCoverConfig(product?.calculator_config?.cover || {});

const hasCoverEnabled = (product, values = {}) => {
  const cover = getCoverConfig(product);
  return Boolean(isPageCalculatorProduct(product) && cover.enabled && Number(values?.[cover.enabled_variable]) > 0);
};

const getCoverInactiveKeys = (product, values = {}) => {
  const cover = getCoverConfig(product);
  if (!isPageCalculatorProduct(product) || !cover.enabled || hasCoverEnabled(product, values)) return new Set();
  return new Set([
    cover.front_colors_variable,
    cover.back_colors_variable,
    cover.plate_mode_variable,
    cover.plates_total_variable,
    cover.print_sheets_variable,
    cover.mother_sheet_variable,
    cover.spoilage_variable,
    cover.sheet_width_variable,
    cover.sheet_height_variable,
    cover.pages_per_sheet_variable,
    'largotapa',
    'anchotapa',
    'gramajetapa',
    'papeltapa',
    'preparaciontapa',
    'planchatapa',
    'papelcalctapa',
    'tintacalctapa',
    'impresiontapa',
    'guillotinatapa',
    'empaquetadotapa',
    'costotapa',
  ].filter(Boolean));
};

const applyCoverImposition = (product, values, coverImposition) => {
  if (!hasCoverEnabled(product, values) || !coverImposition?.choice || !coverImposition?.config) return values;
  const cover = getCoverConfig(product);
  const derived = getImpositionDerivedValues(coverImposition.choice, coverImposition.config, {
    sheets_variable: cover.print_sheets_variable,
    mother_sheet_variable: cover.mother_sheet_variable,
    spoilage_variable: cover.spoilage_variable,
    sheet_width_variable: cover.sheet_width_variable,
    sheet_height_variable: cover.sheet_height_variable,
    pages_per_sheet_variable: cover.pages_per_sheet_variable,
  });
  return { ...values, ...derived };
};

const buildMagazineBudgetConfig = (product, interiorCount) => {
  const config = product?.calculator_config || {};
  const elements = normalizeMagazineElements(config);
  if (!isPageCalculatorProduct(product) || elements.length === 0) {
    return product?.calculator_config || {};
  }
  const count = product?.calculator_type === PRODUCT_CALCULATOR_CUADERNO
    ? 1
    : Math.max(1, Math.min(Number(interiorCount) || 1, elements.length));
  return {
    ...config,
    calculator_type: product?.calculator_type,
    cover: normalizeCoverConfig(config.cover || {}),
    elements: elements.slice(0, count).map((element, index) => ({
      ...element,
      pages_per_sheet_variable: element.pages_per_sheet_variable || `paginasporpliegoint${index + 1}`,
      mother_sheet_variable: element.mother_sheet_variable || `hojamadreint${index + 1}`,
      spoilage_variable: element.spoilage_variable || `desperdicioint${index + 1}`,
      sheet_width_variable: element.sheet_width_variable || `anchohojaint${index + 1}`,
      sheet_height_variable: element.sheet_height_variable || `largohojaint${index + 1}`,
    })),
  };
};

const getMagazineElementKey = (element, index) => element.page_count_variable || `interior-${index + 1}`;

const getInactiveMagazineKeys = (product, interiorCount) => {
  const config = product?.calculator_config || {};
  const elements = normalizeMagazineElements(config);
  if (!isPageCalculatorProduct(product)) return new Set();
  const count = Math.max(1, Math.min(Number(interiorCount) || 1, elements.length || 1));
  const keys = new Set();
  elements.slice(count).forEach((element, offset) => {
    const index = count + offset + 1;
    [
      element.page_count_variable,
      element.front_colors_variable,
      element.back_colors_variable,
      element.pages_per_sheet_variable,
      element.mother_sheet_variable,
      element.spoilage_variable,
      element.sheet_width_variable,
      element.sheet_height_variable,
      element.distinct_sheets_variable,
      element.faces_variable,
      element.plates_variable,
      element.print_sheets_variable,
      `paginasint${index}`,
      `gramajeint${index}`,
      `papelint${index}`,
      `colorint${index}frente`,
      `colorint${index}dorso`,
      `papelint${index}calc`,
      `tintaint${index}`,
      `paginasporpliegoint${index}`,
      `hojamadreint${index}`,
      `desperdicioint${index}`,
      `anchohojaint${index}`,
      `largohojaint${index}`,
      `pliegosint${index}`,
      `carasint${index}`,
      `chapasint${index}`,
      `tiradaint${index}`,
    ].filter(Boolean).forEach(key => keys.add(key));
  });
  return keys;
};

const applyMagazineBudgetValues = (product, interiorCount, values) => {
  const next = { ...(values || {}) };
  getInactiveMagazineKeys(product, interiorCount).forEach(key => {
    next[key] = /^paginasporpliegoint\d+$/.test(key) ? 1 : 0;
  });
  return next;
};

const applyMagazinePagesPerSheet = (product, values, impositionChoice, impositionConfig) => {
  if (!isPageCalculatorProduct(product) || !impositionChoice) return values;
  const key = product.calculator_config?.pages_per_sheet_variable || 'paginasporpliego';
  if (!key) return values;
  const frontItems = Number(impositionChoice.items_per_sheet) || 0;
  if (frontItems <= 0) return values;
  const faces = impositionConfig?.doubleSided ? 2 : 1;
  return {
    ...values,
    [key]: frontItems * faces,
  };
};

const applyMagazineInteriorImpositions = (calculatorConfig, values, choices = {}) => {
  let next = { ...(values || {}) };
  const isNotebook = calculatorConfig?.calculator_type === PRODUCT_CALCULATOR_CUADERNO;
  (calculatorConfig?.elements || []).forEach((element, index) => {
    const key = getMagazineElementKey(element, index);
    const saved = choices[key];
    if (!saved?.choice || !saved?.config) return;
    const derived = getImpositionDerivedValues(saved.choice, saved.config, {
      sheets_variable: element.print_sheets_variable || `tiradaint${index + 1}`,
      mother_sheet_variable: element.mother_sheet_variable || `hojamadreint${index + 1}`,
      spoilage_variable: element.spoilage_variable || `desperdicioint${index + 1}`,
      sheet_width_variable: element.sheet_width_variable || `anchohojaint${index + 1}`,
      sheet_height_variable: element.sheet_height_variable || `largohojaint${index + 1}`,
      pages_per_sheet_variable: element.pages_per_sheet_variable || `paginasporpliegoint${index + 1}`,
    });
    const faceMultiplier = !isNotebook && saved.config?.doubleSided ? 2 : 1;
    const pagesPerSheetKey = element.pages_per_sheet_variable || `paginasporpliegoint${index + 1}`;
    const printSheetsKey = element.print_sheets_variable || `tiradaint${index + 1}`;
    const motherSheetsKey = element.mother_sheet_variable || `hojamadreint${index + 1}`;
    const spoilageKey = element.spoilage_variable || `desperdicioint${index + 1}`;
    if (saved.choice.items_per_sheet) {
      const pagesPerSheet = Number(saved.choice.items_per_sheet) * faceMultiplier;
      derived[pagesPerSheetKey] = pagesPerSheet;
      const quantityKey = calculatorConfig.quantity_variable || 'cantidad';
      const quantity = Number(next[quantityKey]) || 0;
      const pages = Number(next[element.page_count_variable]) || 0;
      const generatedSheetsPerMother = Math.max(Number(saved.config?.sourceCut?.sheetCount) || 1, 1);
      const spoilageMotherSheets = Math.max(
        0,
        Math.ceil(Number(saved.config?.spoilageSheets ?? saved.config?.spoilage_sheets) || 0)
      );
      if (quantity > 0 && pages > 0 && pagesPerSheet > 0) {
        const basePrintSheets = Math.ceil((quantity * pages) / pagesPerSheet);
        derived[printSheetsKey] = basePrintSheets + (spoilageMotherSheets * generatedSheetsPerMother);
        derived[motherSheetsKey] = Math.ceil(basePrintSheets / generatedSheetsPerMother) + spoilageMotherSheets;
        derived[spoilageKey] = spoilageMotherSheets;
      }
    }
    next = { ...next, ...derived };
  });
  return next;
};

const BudgetCreator = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialClientId = searchParams.get('client') || '';

  const [clients, setClients] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [clientSearchOpen, setClientSearchOpen] = useState(false);
  const [clientSearchLoading, setClientSearchLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [clientId, setClientId] = useState(initialClientId);
  const [productId, setProductId] = useState('');
  const [values, setValues] = useState({});
  const [profitPercentage, setProfitPercentage] = useState(0);
  const [additionalCost, setAdditionalCost] = useState('');
  const [observations, setObservations] = useState('');
  const [magazineInteriorCount, setMagazineInteriorCount] = useState(1);

  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const [impositionChoice, setImpositionChoice] = useState(null);
  const [impositionConfig, setImpositionConfig] = useState(null);
  const [magazineImpositions, setMagazineImpositions] = useState({});
  const [invoiceImpositions, setInvoiceImpositions] = useState({});
  const [coverImposition, setCoverImposition] = useState(null);

  const formatClientSearchLabel = useCallback((client) => {
    if (!client) return '';
    const ids = [client.fixed_id, client.optional_id, client.document].filter(Boolean).join(' / ');
    return ids ? `${client.name} - ${ids}` : client.name || '';
  }, []);

  useEffect(() => {
    Promise.all([api.get('/clients', { params: { limit: 80 } }), api.get('/products')])
      .then(([cRes, pRes]) => {
        setClients(cRes.data || []);
        setClientOptions(cRes.data || []);
        setProducts(pRes.data);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    let cancelled = false;
    const search = clientSearch.trim();
    const timer = setTimeout(() => {
      setClientSearchLoading(true);
      api.get('/clients', { params: { q: search, limit: 80 } })
        .then((res) => {
          if (cancelled) return;
          setClientOptions(res.data || []);
        })
        .catch(() => {
          if (cancelled) return;
          setClientOptions([]);
        })
        .finally(() => {
          if (!cancelled) setClientSearchLoading(false);
        });
    }, search ? 180 : 0);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [clientSearch]);

  const selectedClient = useMemo(
    () => [...clientOptions, ...clients].find(c => c.id === clientId),
    [clientOptions, clients, clientId]
  );

  useEffect(() => {
    if (!initialClientId || selectedClient) return;
    let cancelled = false;
    api.get(`/clients/${initialClientId}`)
      .then((res) => {
        if (cancelled || !res.data) return;
        setClients(prev => [res.data, ...prev.filter(c => c.id !== res.data.id)]);
        setClientOptions(prev => [res.data, ...prev.filter(c => c.id !== res.data.id)]);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [initialClientId, selectedClient]);

  useEffect(() => {
    if (selectedClient && clientId) {
      setClientSearch(formatClientSearchLabel(selectedClient));
    }
  }, [selectedClient, clientId, formatClientSearchLabel]);

  const handleClientSearchChange = (e) => {
    setClientSearch(e.target.value);
    setClientId('');
    setClientSearchOpen(true);
  };

  const chooseClient = (client) => {
    setClientId(client.id);
    setClientSearch(formatClientSearchLabel(client));
    setClientSearchOpen(false);
  };

  const selectedProduct = useMemo(
    () => products.find(p => p.id === productId),
    [products, productId]
  );
  const selectedVariables = useMemo(() => (
    selectedProduct
      ? [
        ...(selectedProduct.global_variables || []).map(v => ({ ...v, global: true })),
        ...(selectedProduct.variables || []),
      ]
      : []
  ), [selectedProduct]);
  const selectedImpositionConfig = useMemo(() => (
    selectedProduct?.imposition_config
      ? normalizeImpositionConfig(selectedProduct.imposition_config, selectedVariables)
      : null
  ), [selectedProduct, selectedVariables]);
  const selectedMagazineElements = useMemo(
    () => normalizeMagazineElements(selectedProduct?.calculator_config || {}),
    [selectedProduct]
  );
  const selectedCalculatorConfig = useMemo(
    () => buildMagazineBudgetConfig(selectedProduct, magazineInteriorCount),
    [selectedProduct, magazineInteriorCount]
  );
  const activeInvoiceCopies = useMemo(
    () => getActiveInvoiceCopies(selectedProduct, values, selectedVariables),
    [selectedProduct, selectedVariables, values]
  );
  const inputVariables = useMemo(() => {
    const impositionOutputKeys = new Set(
      selectedImpositionConfig ? getImpositionOutputKeys(selectedImpositionConfig) : []
    );
    const inactiveMagazineKeys = getInactiveMagazineKeys(selectedProduct, magazineInteriorCount);
    const inactiveInvoiceKeys = getInactiveInvoiceKeys(selectedProduct, values, selectedVariables);
    const inactiveCoverKeys = getCoverInactiveKeys(selectedProduct, values);
    return selectedVariables.filter(v => (
      v.type !== 'calculated'
      && !impositionOutputKeys.has(v.key)
      && !inactiveMagazineKeys.has(v.key)
      && !inactiveInvoiceKeys.has(v.key)
      && !inactiveCoverKeys.has(v.key)
      && v.show_in_budget !== false
    ));
  }, [magazineInteriorCount, selectedImpositionConfig, selectedProduct, selectedVariables, values]);

  // Reset values when product changes
  useEffect(() => {
    if (selectedProduct) {
      const defaults = {};
      selectedVariables
        .filter(v => v.type !== 'calculated' && v.show_in_budget !== false)
        .forEach(v => {
        if (v.type === 'number') defaults[v.key] = v.default_value ?? 0;
        else if (v.type === 'select' && v.options?.length) defaults[v.key] = v.options[0].label;
        else if (v.type === 'checkbox') defaults[v.key] = false;
      });
      setProfitPercentage(selectedProduct.profit_percentage ?? 0);
      setAdditionalCost('');
      setMagazineInteriorCount(isPageCalculatorProduct(selectedProduct) ? 1 : 1);
      setMagazineImpositions({});
      setInvoiceImpositions({});
      setCoverImposition(null);
      setValues(defaults);
      setPreview(null);
    }
  }, [selectedProduct, selectedVariables]);

  useEffect(() => {
    if (!hasCoverEnabled(selectedProduct, values)) {
      setCoverImposition(null);
    }
  }, [selectedProduct, values]);

  useEffect(() => {
    setMagazineImpositions(current => {
      if (!isPageCalculatorProduct(selectedProduct)) return {};
      const allowed = new Set((selectedCalculatorConfig.elements || []).map(getMagazineElementKey));
      return Object.fromEntries(Object.entries(current || {}).filter(([key]) => allowed.has(key)));
    });
  }, [selectedProduct, selectedCalculatorConfig]);

  useEffect(() => {
    setInvoiceImpositions(current => {
      if (selectedProduct?.calculator_type !== PRODUCT_CALCULATOR_FACTURAS) return {};
      const allowed = new Set(activeInvoiceCopies.map(copy => copy.key));
      return Object.fromEntries(Object.entries(current || {}).filter(([key]) => allowed.has(key)));
    });
  }, [activeInvoiceCopies, selectedProduct]);

  // Auto-calculate preview when values change
  const calculatePreview = useCallback(async () => {
    if (!selectedProduct) return;
    setPreviewError('');
    try {
      const valuesWithMagazineChoice = applyInvoiceBudgetValues(
        selectedProduct,
        applyMagazineBudgetValues(selectedProduct, magazineInteriorCount, values),
        selectedVariables
      );
      const baseCalculationValues = selectedProduct?.imposition_config
        ? applyMagazinePagesPerSheet(
          selectedProduct,
          applyImpositionDerivedValues(
          valuesWithMagazineChoice,
          impositionChoice,
          impositionConfig,
          selectedImpositionConfig
          ),
          impositionChoice,
          impositionConfig
        )
        : valuesWithMagazineChoice;
      const calculationValues = isPageCalculatorProduct(selectedProduct)
        ? applyCoverImposition(
          selectedProduct,
          applyMagazineInteriorImpositions(selectedCalculatorConfig, baseCalculationValues, magazineImpositions),
          coverImposition
        )
        : applyInvoiceCopyImpositions(selectedProduct, baseCalculationValues, invoiceImpositions, selectedVariables);
      const { data } = await api.post('/products/preview-formula', {
        variables: selectedVariables,
        formula: selectedProduct.formula,
        profit_percentage: profitPercentage,
        additional_cost: Number(additionalCost) || 0,
        values: calculationValues,
        production_type: selectedProduct.production_type,
        calculator_type: selectedProduct.calculator_type,
        calculator_config: selectedCalculatorConfig,
        imposition_config: selectedProduct.imposition_config,
        imposition_choice: impositionChoice ? { choice: impositionChoice, config: impositionConfig } : null,
        magazine_imposition_choices: magazineImpositions,
        cover_imposition_choice: coverImposition,
        invoice_imposition_choices: invoiceImpositions,
        production_time_config: selectedProduct.production_time_config,
      });
      setPreview(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setPreviewError(typeof detail === 'string' ? detail : 'Error al calcular');
      setPreview(null);
    }
  }, [
    impositionChoice,
    impositionConfig,
    additionalCost,
    profitPercentage,
    selectedImpositionConfig,
    selectedCalculatorConfig,
    selectedProduct,
    selectedVariables,
    values,
    magazineInteriorCount,
    magazineImpositions,
    coverImposition,
    invoiceImpositions,
  ]);

  useEffect(() => {
    if (!selectedProduct) return;
    const timer = setTimeout(() => calculatePreview(), 350);
    return () => clearTimeout(timer);
  }, [calculatePreview, selectedProduct]);

  const handleSave = async () => {
    setSaveError('');
    if (!clientId) {
      setSaveError('Elegí un cliente');
      return;
    }
    if (!productId) {
      setSaveError('Elegí un producto');
      return;
    }
    setSaving(true);
    try {
      const valuesWithMagazineChoice = applyInvoiceBudgetValues(
        selectedProduct,
        applyMagazineBudgetValues(selectedProduct, magazineInteriorCount, values),
        selectedVariables
      );
      const baseFinalValues = selectedProduct?.imposition_config
        ? applyMagazinePagesPerSheet(
          selectedProduct,
          applyImpositionDerivedValues(
          valuesWithMagazineChoice,
          impositionChoice,
          impositionConfig,
          selectedImpositionConfig
          ),
          impositionChoice,
          impositionConfig
        )
        : valuesWithMagazineChoice;
      const finalValues = isPageCalculatorProduct(selectedProduct)
        ? applyCoverImposition(
          selectedProduct,
          applyMagazineInteriorImpositions(selectedCalculatorConfig, baseFinalValues, magazineImpositions),
          coverImposition
        )
        : applyInvoiceCopyImpositions(selectedProduct, baseFinalValues, invoiceImpositions, selectedVariables);
      const impositionDerivedValues = selectedProduct?.imposition_config
        ? getImpositionDerivedValues(impositionChoice, impositionConfig, selectedImpositionConfig)
        : {};
      const sheetsVariable = selectedImpositionConfig?.sheets_variable;
      const motherSheetVariable = selectedImpositionConfig?.mother_sheet_variable;
      const spoilageVariable = selectedImpositionConfig?.spoilage_variable;
      const { data } = await api.post('/budgets', {
        client_id: clientId,
        product_id: productId,
        variable_values: finalValues,
        profit_percentage: Number(profitPercentage) || 0,
        additional_cost: Number(additionalCost) || 0,
        observations,
        calculator_config_override: isPageCalculatorProduct(selectedProduct)
          ? selectedCalculatorConfig
          : null,
        magazine_imposition_choices: isPageCalculatorProduct(selectedProduct)
          ? magazineImpositions
          : {},
        cover_imposition_choice: isPageCalculatorProduct(selectedProduct)
          ? coverImposition
          : null,
        invoice_imposition_choices: selectedProduct.calculator_type === PRODUCT_CALCULATOR_FACTURAS
          ? invoiceImpositions
          : {},
        imposition_choice: impositionChoice ? {
          id: impositionChoice.id,
          label: impositionChoice.label,
          items_per_sheet: impositionChoice.items_per_sheet,
          base_sheets_needed: impositionChoice.sheets_needed,
          sheets_needed: Number(impositionDerivedValues[sheetsVariable]) || impositionChoice.sheets_needed,
          mother_sheets_needed: Number(impositionDerivedValues[motherSheetVariable]) || 0,
          spoilage_sheets: Number(impositionDerivedValues[spoilageVariable]) || 0,
          usage: impositionChoice.usage,
          pieces: impositionChoice.pieces,
          config: impositionConfig,
          sheet_cut: impositionConfig?.sourceCut || null,
        } : null,
      });
      navigate(`/presupuestos/${data.id}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setSaveError(typeof detail === 'string' ? detail : 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <Sidebar activePath={location.pathname} />
        <main className="main-content">
          <div className="empty-state">Cargando...</div>
        </main>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <Sidebar activePath={location.pathname} />

      <main className="main-content">
        <div className="page-header-row">
          <div>
            <h1 data-testid="new-budget-title">Nuevo Presupuesto</h1>
            <p className="dashboard-subtitle">
              Elegí cliente y producto, ingresá los valores y guardá el presupuesto
            </p>
          </div>
          <button
            className="btn-secondary"
            onClick={() => navigate('/presupuestos')}
            data-testid="cancel-budget-button"
          >
            Volver
          </button>
        </div>

        {/* ===== CLIENTE + PRODUCTO ===== */}
        <div className="card editor-card">
          <h3 className="card-title">Cliente y Producto</h3>

          <div className="form-group">
            <label className="form-label">Cliente *</label>
            {clients.length === 0 ? (
              <div className="empty-state">
                No hay clientes registrados.{' '}
                <button className="btn-link" onClick={() => navigate('/clientes')}>Crear cliente</button>
              </div>
            ) : (
              <div className="client-autocomplete">
                <input
                  className="form-input"
                  value={clientSearch}
                  onChange={handleClientSearchChange}
                  onFocus={() => setClientSearchOpen(true)}
                  onBlur={() => setTimeout(() => setClientSearchOpen(false), 150)}
                  placeholder="Buscar por nombre, ID, documento, telefono o email"
                  autoComplete="off"
                  data-testid="budget-client-select"
                />
                {clientSearchOpen && (
                  <div className="client-autocomplete-results">
                    {clientSearchLoading ? (
                      <div className="client-autocomplete-empty">Buscando clientes...</div>
                    ) : clientOptions.length === 0 ? (
                      <div className="client-autocomplete-empty">No se encontraron clientes</div>
                    ) : (
                      clientOptions.map((client) => (
                        <button
                          key={client.id}
                          type="button"
                          className={`client-autocomplete-option ${client.id === clientId ? 'active' : ''}`}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            chooseClient(client);
                          }}
                        >
                          <span>{client.name}</span>
                          <small>
                            {[client.fixed_id, client.optional_id, client.document, client.phone, client.email]
                              .filter(Boolean)
                              .join(' - ') || 'Sin datos adicionales'}
                          </small>
                        </button>
                      ))
                    )}
                  </div>
                )}
                {clientSearch && !clientId && (
                  <div className="client-autocomplete-hint">Selecciona un cliente de la lista para continuar.</div>
                )}
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Producto *</label>
            {products.length === 0 ? (
              <div className="empty-state">
                No hay productos registrados.{' '}
                <button className="btn-link" onClick={() => navigate('/productos/nuevo')}>Crear producto</button>
              </div>
            ) : (
              <select
                className="form-input"
                value={productId}
                onChange={(e) => setProductId(e.target.value)}
                data-testid="budget-product-select"
              >
                <option value="">-- Elegir producto --</option>
                {products.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* ===== VARIABLES ===== */}
        {selectedProduct && (
          <div className="card editor-card">
            <h3 className="card-title">Valores para "{selectedProduct.name}"</h3>

            {selectedProduct?.calculator_type === PRODUCT_CALCULATOR_REVISTA && selectedMagazineElements.length > 0 && (
              <div className="form-group">
                <label className="form-label">Cantidad de interiores</label>
                <select
                  className="form-input"
                  value={magazineInteriorCount}
                  onChange={(e) => setMagazineInteriorCount(Number(e.target.value) || 1)}
                >
                  {selectedMagazineElements.map((_, index) => (
                    <option key={index + 1} value={index + 1}>
                      {index + 1} interior{index + 1 > 1 ? 'es' : ''}
                    </option>
                  ))}
                </select>
                <div className="input-hint">
                  Esta eleccion es solo para este presupuesto. El producto queda como plantilla.
                </div>
              </div>
            )}

            {inputVariables.length === 0 ? (
              <div className="empty-state">Este producto no tiene variables definidas</div>
            ) : (
              inputVariables.map(v => (
                <div key={`${v.global ? 'global' : 'product'}-${v.key}`} className="form-group">
                  <label className="form-label">
                    {v.label || v.key} {v.global && <span className="unit-hint">(global)</span>} {v.unit && <span className="unit-hint">({v.unit})</span>}
                  </label>

                  {v.type === 'number' && (
                    <input
                      type="number"
                      className="form-input"
                      value={values[v.key] ?? ''}
                      onChange={(e) => setValues({ ...values, [v.key]: e.target.value })}
                      data-testid={`budget-var-${v.key}`}
                    />
                  )}

                  {v.type === 'select' && (
                    <select
                      className="form-input"
                      value={values[v.key] || ''}
                      onChange={(e) => setValues({ ...values, [v.key]: e.target.value })}
                      data-testid={`budget-var-${v.key}`}
                    >
                      {(v.options || []).map((opt, oi) => (
                        <option key={oi} value={opt.label}>
                          {formatSelectOptionLabel(v, opt)}
                        </option>
                      ))}
                    </select>
                  )}

                  {v.type === 'checkbox' && (
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={Boolean(values[v.key])}
                        onChange={(e) => setValues({ ...values, [v.key]: e.target.checked })}
                        data-testid={`budget-var-${v.key}`}
                      />
                      {' '}
                      {v.unit === '$' ? `Aplicar (agrega ${formatMoney(v.checked_value)})` : 'Activar'}
                    </label>
                  )}
                </div>
              ))
            )}

            <div className="form-group">
              <label className="form-label">Porcentaje de ganancia (%)</label>
              <input
                type="number"
                className="form-input"
                value={profitPercentage}
                onChange={(e) => setProfitPercentage(e.target.value)}
                step="0.1"
                data-testid="budget-profit-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Costo adicional ($)</label>
              <input
                type="number"
                className="form-input"
                value={additionalCost}
                onChange={(e) => setAdditionalCost(e.target.value)}
                min="0"
                step="0.01"
                placeholder="0"
                data-testid="budget-additional-cost-input"
              />
            </div>

            {previewError && <div className="login-error" data-testid="budget-preview-error">{previewError}</div>}

            {preview && (
              <div className="test-result" data-testid="budget-preview">
                <div className="test-result-row">
                  <span>Costo base:</span>
                  <span className="mono">{formatMoney(preview.base_cost)}</span>
                </div>
                <div className="test-result-row">
                  <span>Ganancia ({preview.profit_percentage}%):</span>
                  <span className="mono">{formatMoney(preview.profit_amount ?? 0)}</span>
                </div>
                {Number(preview.additional_cost || 0) > 0 && (
                  <div className="test-result-row">
                    <span>Costo adicional:</span>
                    <span className="mono">{formatMoney(preview.additional_cost)}</span>
                  </div>
                )}
                <div className="test-result-row test-result-final">
                  <span>Precio final:</span>
                  <span className="mono" data-testid="budget-preview-final">{formatMoney(preview.final_price)}</span>
                </div>
                {preview.production_time && (
                  <>
                    <div className="test-result-row">
                      <span>Variable tiempo:</span>
                      <span className="mono">
                        {preview.production_time.total_hours.toLocaleString('es-AR', { maximumFractionDigits: 4 })} h
                      </span>
                    </div>
                    <div className="test-result-row">
                      <span>Tiempo estimado:</span>
                      <span className="mono">
                        {preview.production_time.total_minutes.toLocaleString('es-AR', { maximumFractionDigits: 0 })} min
                      </span>
                    </div>
                    <div className="test-result-row">
                      <span>Variable tiempo_minutos:</span>
                      <span className="mono">
                        {preview.production_time.total_minutes.toLocaleString('es-AR', { maximumFractionDigits: 2 })} min
                      </span>
                    </div>
                  </>
                )}
                <CalculationBreakdown
                  preview={preview}
                  variables={selectedVariables}
                  formula={selectedProduct.formula}
                />
              </div>
            )}
          </div>
        )}

        {/* ===== IMPOSITION ===== */}
        {selectedImpositionConfig
          && !isPageCalculatorProduct(selectedProduct)
          && selectedProduct?.calculator_type !== PRODUCT_CALCULATOR_FACTURAS && (() => {
          const cfg = selectedImpositionConfig;
          const rawW = values[cfg.width_variable];
          const rawH = values[cfg.height_variable];
          const rawQ = values[cfg.quantity_variable];
          const itemW = Number(rawW) || 0;
          const itemH = Number(rawH) || 0;
          const qty = Number(rawQ) || 1;
          const canRender = itemW > 0 && itemH > 0;
          return (
            <div className="card editor-card">
              <h3 className="card-title">Imposición de Pliego</h3>
              <p className="dashboard-subtitle" style={{ marginBottom: '1rem' }}>
                Elegí la opción de imposición que preferís. La elegida quedará guardada en el presupuesto.
              </p>
              {!canRender ? (
                <div className="empty-state">
                  Ingresá el ancho y alto de la pieza para ver las opciones de imposición
                </div>
              ) : (
                <ImpositionSelector
                  value={impositionChoice}
                  onChange={(opt, config) => {
                    setImpositionChoice(opt);
                    setImpositionConfig(config);
                    setValues(current => applyMagazinePagesPerSheet(
                      selectedProduct,
                      applyImpositionDerivedValues(current, opt, config, cfg),
                      opt,
                      config
                    ));
                  }}
                  defaultConfig={{
                    sheetWidth: cfg.sheet_width,
                    sheetHeight: cfg.sheet_height,
                    gripperTop: cfg.gripper_top,
                    gripperBottom: cfg.gripper_bottom,
                    marginLeft: cfg.margin_left,
                    marginRight: cfg.margin_right,
                    bleed: cfg.bleed,
                    separation: cfg.separation,
                    spoilageSheets: cfg.spoilage_sheets,
                    allowRotation: cfg.allow_rotation,
                    doubleSided: cfg.double_sided,
                  }}
                  itemWidth={itemW}
                  itemHeight={itemH}
                  quantity={qty}
                />
              )}
            </div>
          );
        })()}

        {selectedImpositionConfig && selectedProduct?.calculator_type === PRODUCT_CALCULATOR_FACTURAS && (() => {
          const cfg = selectedImpositionConfig;
          const rawW = values[cfg.width_variable];
          const rawH = values[cfg.height_variable];
          const rawQ = values[cfg.quantity_variable];
          const itemW = Number(rawW) || 0;
          const itemH = Number(rawH) || 0;
          const qty = Number(rawQ) || 1;
          const canRender = itemW > 0 && itemH > 0;
          return (
            <div className="card editor-card">
              <h3 className="card-title">Imposicion por copia</h3>
              <p className="dashboard-subtitle" style={{ marginBottom: '1rem' }}>
                Elegi una imposicion para cada copia activa. Cada una puede usar otra hoja madre segun el papel disponible.
              </p>
              {!canRender ? (
                <div className="empty-state">
                  Ingresa el ancho y alto de la factura para ver las opciones de imposicion.
                </div>
              ) : (
                activeInvoiceCopies.map((copy) => {
                  const saved = invoiceImpositions[copy.key];
                  return (
                    <div key={copy.key} className="card editor-card">
                      <h4 className="card-title">{copy.label}</h4>
                      <ImpositionSelector
                        value={saved?.choice || null}
                        onChange={(opt, config) => {
                          setInvoiceImpositions(current => ({
                            ...current,
                            [copy.key]: opt ? {
                              choice: {
                                id: opt.id,
                                label: opt.label,
                                items_per_sheet: opt.items_per_sheet,
                                base_sheets_needed: opt.sheets_needed,
                                sheets_needed: opt.sheets_needed,
                                usage: opt.usage,
                                pieces: opt.pieces,
                                sheet_cut: config?.sourceCut || null,
                              },
                              config,
                            } : null,
                          }));
                        }}
                        defaultConfig={{
                          sheetWidth: cfg.sheet_width,
                          sheetHeight: cfg.sheet_height,
                          gripperTop: cfg.gripper_top,
                          gripperBottom: cfg.gripper_bottom,
                          marginLeft: cfg.margin_left,
                          marginRight: cfg.margin_right,
                          bleed: cfg.bleed,
                          separation: cfg.separation,
                          spoilageSheets: cfg.spoilage_sheets,
                          allowRotation: cfg.allow_rotation,
                          doubleSided: false,
                        }}
                        itemWidth={itemW}
                        itemHeight={itemH}
                        quantity={qty}
                      />
                    </div>
                  );
                })
              )}
            </div>
          );
        })()}

        {selectedImpositionConfig && isPageCalculatorProduct(selectedProduct) && (() => {
          const cfg = selectedImpositionConfig;
          const rawW = values[cfg.width_variable];
          const rawH = values[cfg.height_variable];
          const itemW = Number(rawW) || 0;
          const itemH = Number(rawH) || 0;
          const qty = Number(values[cfg.quantity_variable]) || 1;
          const canRender = itemW > 0 && itemH > 0;
          return (
            <div className="card editor-card">
              <h3 className="card-title">Imposicion por Interior</h3>
              <p className="dashboard-subtitle" style={{ marginBottom: '1rem' }}>
                Elegi una imposicion para cada interior activo. Cada una puede usar otra hoja madre o corte.
              </p>
              {!canRender ? (
                <div className="empty-state">
                  Ingresa el ancho y alto para ver las opciones de imposicion.
                </div>
              ) : (
                (selectedCalculatorConfig.elements || []).map((element, index) => {
                  const elementKey = getMagazineElementKey(element, index);
                  const saved = magazineImpositions[elementKey];
                  const elementUnits = Number(values[element.page_count_variable]) || 1;
                  const selectorQuantity = selectedProduct?.calculator_type === PRODUCT_CALCULATOR_CUADERNO
                    ? qty * elementUnits
                    : qty;
                  return (
                    <div key={elementKey} className="card editor-card">
                      <h4 className="card-title">{element.label || `Interior ${index + 1}`}</h4>
                      <ImpositionSelector
                        value={saved?.choice || null}
                        onChange={(opt, config) => {
                          setMagazineImpositions(current => ({
                            ...current,
                            [elementKey]: opt ? {
                              choice: {
                                id: opt.id,
                                label: opt.label,
                                items_per_sheet: opt.items_per_sheet,
                                base_sheets_needed: opt.sheets_needed,
                                sheets_needed: opt.sheets_needed,
                                usage: opt.usage,
                                pieces: opt.pieces,
                                sheet_cut: config?.sourceCut || null,
                              },
                              config,
                            } : null,
                          }));
                        }}
                        defaultConfig={{
                          sheetWidth: cfg.sheet_width,
                          sheetHeight: cfg.sheet_height,
                          gripperTop: cfg.gripper_top,
                          gripperBottom: cfg.gripper_bottom,
                          marginLeft: cfg.margin_left,
                          marginRight: cfg.margin_right,
                          bleed: cfg.bleed,
                          separation: cfg.separation,
                          spoilageSheets: cfg.spoilage_sheets,
                          allowRotation: cfg.allow_rotation,
                          doubleSided: true,
                        }}
                        itemWidth={itemW}
                        itemHeight={itemH}
                        quantity={selectorQuantity}
                        doubleSidedCountsAsExtraItems={selectedProduct?.calculator_type !== PRODUCT_CALCULATOR_CUADERNO}
                      />
                    </div>
                  );
                })
              )}
            </div>
          );
        })()}

        {selectedImpositionConfig && hasCoverEnabled(selectedProduct, values) && (() => {
          const cfg = selectedImpositionConfig;
          const cover = getCoverConfig(selectedProduct);
          const itemW = Number(values.anchotapa) || Number(values[cfg.width_variable]) || 0;
          const itemH = Number(values.largotapa) || Number(values[cfg.height_variable]) || 0;
          const qty = Number(values[cfg.quantity_variable]) || 1;
          const canRender = itemW > 0 && itemH > 0;
          return (
            <div className="card editor-card">
              <h3 className="card-title">Imposicion de Tapa</h3>
              <p className="dashboard-subtitle" style={{ marginBottom: '1rem' }}>
                La tapa usa su propia hoja madre, corte y distribucion. Se calcula como un trabajo Offset simple.
              </p>
              {!canRender ? (
                <div className="empty-state">
                  Ingresa ancho y largo de tapa para ver las opciones de imposicion.
                </div>
              ) : (
                <ImpositionSelector
                  value={coverImposition?.choice || null}
                  onChange={(opt, config) => {
                    setCoverImposition(opt ? {
                      choice: {
                        id: opt.id,
                        label: opt.label,
                        items_per_sheet: opt.items_per_sheet,
                        base_sheets_needed: opt.sheets_needed,
                        sheets_needed: opt.sheets_needed,
                        usage: opt.usage,
                        pieces: opt.pieces,
                        sheet_cut: config?.sourceCut || null,
                      },
                      config,
                    } : null);
                  }}
                  defaultConfig={{
                    sheetWidth: cfg.sheet_width,
                    sheetHeight: cfg.sheet_height,
                    gripperTop: cfg.gripper_top,
                    gripperBottom: cfg.gripper_bottom,
                    marginLeft: cfg.margin_left,
                    marginRight: cfg.margin_right,
                    bleed: cfg.bleed,
                    separation: cfg.separation,
                    spoilageSheets: cfg.spoilage_sheets,
                    allowRotation: cfg.allow_rotation,
                    doubleSided: true,
                  }}
                  itemWidth={itemW}
                  itemHeight={itemH}
                  quantity={qty}
                  outputConfig={{
                    sheets_variable: cover.print_sheets_variable,
                    mother_sheet_variable: cover.mother_sheet_variable,
                    spoilage_variable: cover.spoilage_variable,
                    sheet_width_variable: cover.sheet_width_variable,
                    sheet_height_variable: cover.sheet_height_variable,
                    pages_per_sheet_variable: cover.pages_per_sheet_variable,
                  }}
                />
              )}
            </div>
          );
        })()}

        {/* ===== OBSERVACIONES ===== */}
        {selectedProduct && (
          <div className="card editor-card">
            <h3 className="card-title">Observaciones</h3>
            <div className="form-group">
              <textarea
                className="form-input"
                rows={4}
                value={observations}
                onChange={(e) => setObservations(e.target.value)}
                placeholder="Notas internas, plazos, requerimientos especiales..."
                data-testid="budget-observations"
              />
            </div>
          </div>
        )}

        {/* ===== SAVE ===== */}
        {selectedProduct && (
          <div className="editor-actions">
            {saveError && <div className="login-error" data-testid="budget-save-error">{saveError}</div>}
            <div className="editor-buttons">
              <button className="btn-secondary" onClick={() => navigate('/presupuestos')}>Cancelar</button>
              <button
                className="btn-primary-inline"
                onClick={handleSave}
                disabled={saving || !preview}
                data-testid="save-budget-button"
              >
                {saving ? 'Guardando...' : 'Guardar Presupuesto'}
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default BudgetCreator;
